/* eslint-disable max-lines */
//
//  SDKDimensionToken.ts
//  Supernova SDK
//
//  Created by Jiri Trecak.
//  Copyright © 2021 Supernova. All rights reserved.
//
// --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
// MARK: - Imports
// TODO:fix-sdk-eslint
// eslint-disable-next-line max-classes-per-file
import { SupernovaError } from "../../core/errors/SDKSupernovaError";
import { DTExpressionParser } from "../../tools/design-tokens/utilities/expression/SDKDTExpressionParser";
import { UnreachableCaseError } from "../../utils/UnreachableCaseError";
import { MS_DIMENSION_TOKEN_TYPES, RAW_DIMENSION_TOKEN_TYPES, TokenType, } from "../enums/SDKTokenType";
import { LINE_HEIGHT_UNITS, SIZE_UNITS, Unit, } from "../enums/SDKUnit";
import { Token } from "./SDKToken";
import { v4 as uuidv4 } from "uuid";
// --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
// MARK: -  Object Definition
export class DimensionCategoryToken extends Token {
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Constructor
    constructor(versionId, baseToken, value, alias, properties, propertyValues) {
        super(baseToken, versionId, properties, propertyValues);
        this.value = value;
        if (alias) {
            this.value.referencedTokenId = alias.id;
        }
    }
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Static building
    static create(type, versionId, brandId, name, description, value, alias, properties, propertyValues) {
        var _a;
        const baseToken = {
            // @ts-expect-error TS(2322): Type 'undefined' is not assignable to type 'string... Remove this comment to see the full error message
            id: undefined, // Ommited id will create new token
            persistentId: uuidv4(),
            brandId,
            designSystemVersionId: versionId,
            type,
            meta: {
                name,
                description,
            },
            data: {},
            customPropertyOverrides: [],
        };
        if (value !== null && value !== undefined) {
            const tokenValue = DimensionCategoryToken.dimensionValueFromDefinition(value, type);
            return new this(versionId, baseToken, tokenValue, undefined, properties, propertyValues);
        }
        if (alias) {
            // Aliased value - copy and create raw from reference
            if (alias.tokenType === type) {
                const tokenValue = {
                    unit: Unit.pixels,
                    measure: 0,
                    referencedTokenId: alias.id,
                };
                return new this(versionId, baseToken, tokenValue, undefined, properties, propertyValues);
            }
            // Inline alias, as SN does not support Space referencing Dimension, but TS plugin does
            console.log(`Inlining alias ${alias.id}: ${alias.tokenType} into ${name}:${type}`);
            const tokenValue = {
                unit: DimensionCategoryToken.getCompatibleUnit(alias.value.unit, type),
                measure: DimensionCategoryToken.getCompatibleMeasure(alias.value.unit, type, alias.value.measure),
                referencedTokenId: null,
            };
            const result = new this(versionId, baseToken, tokenValue, undefined, properties, propertyValues);
            result.origin = Object.assign(Object.assign({}, ((_a = result.origin) !== null && _a !== void 0 ? _a : { id: null, name: null, sourceId: null })), { referencePersistentId: alias.id });
            return result;
        }
        throw SupernovaError.fromMessage("Dimension Token must be created using value or alias, but none was provided");
    }
    static getCompatibleUnit(unit, type) {
        switch (type) {
            case TokenType.dimension:
                return unit;
            case TokenType.size:
            case TokenType.space:
            case TokenType.fontSize:
            case TokenType.letterSpacing:
            case TokenType.paragraphSpacing:
            case TokenType.radius:
                return SIZE_UNITS.includes(unit) ? unit : Unit.pixels;
            case TokenType.lineHeight:
                return LINE_HEIGHT_UNITS.includes(unit)
                    ? unit
                    : Unit.raw;
            case TokenType.opacity:
                // TODO: Consider transforming percent measure if it is referenced
                return Unit.raw;
            case TokenType.borderWidth:
                return Unit.pixels;
            case TokenType.duration:
                return Unit.ms;
            case TokenType.zIndex:
                return Unit.raw;
            default:
                throw new UnreachableCaseError(type);
        }
    }
    static getCompatibleMeasure(unit, type, measure) {
        return type === TokenType.opacity && unit === Unit.percent
            ? measure / 100.0
            : measure;
    }
    // If unitless and does not support Pixels use other default: Raw or Ms
    static getDefaultUnit(type, parsedDefinition) {
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        if (RAW_DIMENSION_TOKEN_TYPES.includes(type)) {
            return Unit.raw;
        }
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        if (MS_DIMENSION_TOKEN_TYPES.includes(type)) {
            return Unit.ms;
        }
        if (type === TokenType.lineHeight &&
            parsedDefinition !== undefined &&
            parsedDefinition <= 10) {
            return Unit.raw;
        }
        if (type === TokenType.dimension && parsedDefinition !== undefined) {
            return Unit.raw;
        }
        return Unit.pixels;
    }
    static dimensionValueFromDefinition(definition, type = TokenType.dimension) {
        if (typeof definition === "number") {
            return {
                measure: definition,
                unit: this.getDefaultUnit(type),
                referencedTokenId: null,
            };
        }
        const result = this.parseDimension(definition, type);
        return {
            measure: result.measure,
            unit: result.unit,
            referencedTokenId: null,
        };
    }
    static parseDimension(definition, type) {
        if (typeof definition !== "string") {
            return {
                measure: 1,
                unit: this.getDefaultUnit(type),
            };
        }
        // Use expression parser to handle the expression
        const parsedDefinition = DTExpressionParser.reduceExpressionsToBaseForm(definition);
        if (typeof parsedDefinition === "number") {
            return {
                measure: Number.isNaN(parsedDefinition) ||
                    parsedDefinition === undefined ||
                    parsedDefinition === null
                    ? 0
                    : parsedDefinition,
                unit: this.getDefaultUnit(type, parsedDefinition),
            };
        }
        // Parse out unit
        let measure = parsedDefinition.replace(" ", "");
        let unit = this.getDefaultUnit(type);
        if (parsedDefinition.endsWith("px") || parsedDefinition.endsWith("pt")) {
            measure = measure.substring(0, measure.length - 2);
            unit = Unit.pixels;
        }
        else if (parsedDefinition.endsWith("%")) {
            measure = measure.substring(0, measure.length - 1);
            unit = Unit.percent;
        }
        else if (parsedDefinition.endsWith("em")) {
            measure = measure.substring(0, measure.length - 2);
            unit = Unit.rem;
        }
        else if (parsedDefinition.endsWith("ms")) {
            measure = measure.substring(0, measure.length - 2);
            unit = Unit.ms;
        }
        // Experimental: Units can now be everything, not just pixels. Enable this line to only use pixels
        // unit = Unit.pixels
        // Parse
        let parsedMeasure = parseFloat(measure);
        // Fallback to default unit if specific Dimension subtype doesn't support what was parsed
        const isUnitless = !Number.isNaN(Number(parsedDefinition));
        if ([TokenType.opacity, TokenType.zIndex].includes(type)) {
            if (unit === Unit.percent && type === TokenType.opacity) {
                parsedMeasure /= 100;
            }
            unit = Unit.raw;
        }
        else if ([TokenType.borderWidth].includes(type)) {
            unit = Unit.pixels;
        }
        else if (type === TokenType.duration) {
            unit = Unit.ms;
        }
        else if ([
            TokenType.size,
            TokenType.space,
            TokenType.fontSize,
            TokenType.paragraphSpacing,
            TokenType.letterSpacing,
            TokenType.radius,
        ].includes(type) &&
            !SIZE_UNITS.includes(unit)) {
            unit = Unit.pixels;
        }
        else if (type === TokenType.dimension && isUnitless) {
            // Fallback to raw, for types that support raw and come unitless
            unit = Unit.raw;
        }
        else if (type === TokenType.lineHeight &&
            (!LINE_HEIGHT_UNITS.includes(unit) ||
                (isUnitless && parsedMeasure <= 10))) {
            // If the value of line height is larger than 10, import as pixels
            // Some reasoning: usually, the raw is used for relative values like 1.4, 1.8, 2.5, etc. where pixels are used when define for absolute values like 12px, 16px, 36px etc
            unit = Unit.raw;
        }
        return {
            measure: Number.isNaN(parsedMeasure) ||
                parsedMeasure === undefined ||
                parsedMeasure === null
                ? 0
                : parsedMeasure,
            unit,
        };
    }
    static dimensionValueFromDefinitionOrReference(definition, referenceResolver, tokenType = TokenType.dimension) {
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        if (referenceResolver.valueHasReference(definition)) {
            // TODO:fix-sdk-eslint
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            if (!referenceResolver.isBalancedReference(definition)) {
                // Internal syntax of reference corrupted
                throw new Error(
                // TODO:fix-sdk-eslint
                // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                `Invalid reference syntax in token value: ${definition}`);
            }
            // TODO:fix-sdk-eslint
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            if (referenceResolver.valueIsPureReference(definition)) {
                // When color is pure reference, we can immediately resolve it
                const reference = referenceResolver.lookupReferencedToken(
                // TODO:fix-sdk-eslint
                // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                definition);
                if (!reference) {
                    // @ts-expect-error TS(2322): Type 'undefined' is not assignable to type 'AnyDim... Remove this comment to see the full error message
                    return undefined;
                }
                if (tokenType === reference.tokenType) {
                    return {
                        referencedTokenId: reference.id,
                        measure: reference.value.measure,
                        unit: reference.value.unit,
                    };
                }
                //
                // If tokenType from args !== tokenType resolved, inline
                // Align units, if we reference dimension from Opacity, unit must become raw
                console.log(
                // TODO:fix-sdk-eslint
                // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                `Inlining reference ${reference.id}: ${reference.tokenType} into ${definition}:${tokenType}`);
                return {
                    unit: DimensionCategoryToken.getCompatibleUnit(reference.value.unit, tokenType),
                    measure: DimensionCategoryToken.getCompatibleMeasure(reference.value.unit, tokenType, reference.value.measure),
                    referencedTokenId: null,
                };
            }
            // When color is not a pure reference, we must resolve it further before we can resolve it
            // TODO:fix-sdk-eslint
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            const references = referenceResolver.lookupAllReferencedTokens(definition);
            if (!references) {
                // Still unable to solve the reference, continue looking in some other tokens
                // @ts-expect-error TS(2322): Type 'undefined' is not assignable to type 'AnyDim... Remove this comment to see the full error message
                return undefined;
            }
            // Resolved all internal references
            const resolvedValue = referenceResolver.replaceAllReferencedTokens(
            // TODO:fix-sdk-eslint
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            definition, references);
            return this.dimensionValueFromDefinition(resolvedValue, tokenType);
        }
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        return this.dimensionValueFromDefinition(definition, tokenType);
    }
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Writing
    toString() {
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        return DimensionToken.valueToString(this.value);
    }
    static valueToString(value, options = {
        maxNumberOfDecimals: 2,
        clipTrailingZeroes: true,
    }) {
        const measureString = this.numberToString(value.measure, options);
        return `${measureString}${this.unitToString(value.unit)}`;
    }
    static numberToString(value, options = {
        maxNumberOfDecimals: 2,
        clipTrailingZeroes: true,
    }) {
        let measureString = value.toString();
        if ((options === null || options === void 0 ? void 0 : options.maxNumberOfDecimals) !== undefined) {
            const factor = 10 ** options.maxNumberOfDecimals;
            const adjustedMeasure = Math.round(value * factor) / factor;
            measureString = adjustedMeasure.toFixed(options.maxNumberOfDecimals);
        }
        if (options === null || options === void 0 ? void 0 : options.clipTrailingZeroes) {
            measureString = parseFloat(measureString).toString();
        }
        return `${measureString}`;
    }
    static unitToString(unit) {
        switch (unit) {
            case Unit.pixels:
                return `px`;
            case Unit.percent:
                return `%`;
            case Unit.rem:
                return `rem`;
            case Unit.ms:
                return `ms`;
            case Unit.raw:
                return ``;
            default:
                // TODO:fix-sdk-eslint
                // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                throw new Error(`Unknown unit: ${unit}`);
        }
    }
    toWriteObject() {
        const baseData = this.toBaseWriteObject();
        const specificData = baseData;
        specificData.data = DimensionCategoryToken.valueToWriteObject(this.value);
        return specificData;
    }
    static valueToWriteObject(value) {
        var _a;
        const valueObject = !value.referencedTokenId
            ? {
                measure: Number.isNaN(value.measure) ||
                    value.measure === undefined ||
                    value.measure === null
                    ? 0
                    : value.measure,
                unit: value.unit,
            }
            : undefined;
        return {
            aliasTo: (_a = value.referencedTokenId) !== null && _a !== void 0 ? _a : undefined,
            // @ts-expect-error TS(2322): Type '{ measure: number; unit: Unit; } | undefined... Remove this comment to see the full error message
            value: valueObject,
        };
    }
    static valueToInternalWriteObject(value) {
        // @ts-expect-error TS(2322): Type '{ unit: Unit; measure: number; referencedTok... Remove this comment to see the full error message
        return value
            ? {
                unit: value.unit,
                measure: value.measure,
                referencedTokenId: undefined,
            }
            : null;
    }
}
export class DimensionToken extends DimensionCategoryToken {
    constructor() {
        super(...arguments);
        this.tokenType = TokenType.dimension;
    }
}
export class SizeToken extends DimensionCategoryToken {
    constructor() {
        super(...arguments);
        this.tokenType = TokenType.size;
    }
}
export class SpaceToken extends DimensionCategoryToken {
    constructor() {
        super(...arguments);
        this.tokenType = TokenType.space;
    }
}
export class OpacityToken extends DimensionCategoryToken {
    constructor() {
        super(...arguments);
        this.tokenType = TokenType.opacity;
    }
}
export class FontSizeToken extends DimensionCategoryToken {
    constructor() {
        super(...arguments);
        this.tokenType = TokenType.fontSize;
    }
}
export class LineHeightToken extends DimensionCategoryToken {
    constructor() {
        super(...arguments);
        this.tokenType = TokenType.lineHeight;
    }
}
export class LetterSpacingToken extends DimensionCategoryToken {
    constructor() {
        super(...arguments);
        this.tokenType = TokenType.letterSpacing;
    }
}
export class ParagraphSpacingToken extends DimensionCategoryToken {
    constructor() {
        super(...arguments);
        this.tokenType = TokenType.paragraphSpacing;
    }
}
export class BorderWidthToken extends DimensionCategoryToken {
    constructor() {
        super(...arguments);
        this.tokenType = TokenType.borderWidth;
    }
}
export class RadiusToken extends DimensionCategoryToken {
    constructor() {
        super(...arguments);
        this.tokenType = TokenType.radius;
    }
}
export class DurationToken extends DimensionCategoryToken {
    constructor() {
        super(...arguments);
        this.tokenType = TokenType.duration;
    }
}
export class ZIndexToken extends DimensionCategoryToken {
    constructor() {
        super(...arguments);
        this.tokenType = TokenType.zIndex;
    }
}
export const resolveDimension = (type) => {
    switch (type) {
        case TokenType.dimension:
            return DimensionToken;
        case TokenType.size:
            return SizeToken;
        case TokenType.space:
            return SpaceToken;
        case TokenType.opacity:
            return OpacityToken;
        case TokenType.fontSize:
            return FontSizeToken;
        case TokenType.lineHeight:
            return LineHeightToken;
        case TokenType.letterSpacing:
            return LetterSpacingToken;
        case TokenType.paragraphSpacing:
            return ParagraphSpacingToken;
        case TokenType.borderWidth:
            return BorderWidthToken;
        case TokenType.radius:
            return RadiusToken;
        case TokenType.duration:
            return DurationToken;
        case TokenType.zIndex:
            return ZIndexToken;
        default:
            throw new UnreachableCaseError(type);
    }
};
